import { useState, useEffect } from 'react';
import { updateOrgInfo } from '../../../util/util';
import useFieldErrors from '../../../hooks/useFieldErrors';
import ExpandableTextarea from '../ui/ExpandableTextarea';
import Modal from './Modal';
import ModalActions from './ModalActions';
import FormField, { TextField } from '../ui/FormField';
import { useToast } from '../../ui/Toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INSTAGRAM_HANDLE_RE = /^[A-Za-z0-9._]{1,30}$/;

const CONTACT_FIELDS = [
  { name: 'website', label: 'Website', placeholder: 'https://example.com' },
  { name: 'instagram', label: 'Instagram', placeholder: '@handle' },
  { name: 'contact_email', label: 'Contact Email', type: 'email', placeholder: 'contact@example.com' },
  { name: 'phone', label: 'Phone', type: 'tel', placeholder: '(555) 555-5555' },
];

function normalizeWebsite(raw) {
  if (!/^https?:\/\//i.test(raw)) return `https://${raw}`;
  return raw;
}

function normalizeInstagram(raw) {
  return raw
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
    .replace(/^@/, '')
    .replace(/\/+$/, '');
}

function validateFields({ website, instagram, contact_email, phone }) {
  const errors = {};

  if (website) {
    try {
      new URL(normalizeWebsite(website));
    } catch {
      errors.website = 'Enter a valid website (e.g. example.com)';
    }
  }

  if (instagram && !INSTAGRAM_HANDLE_RE.test(normalizeInstagram(instagram))) {
    errors.instagram = 'Enter a valid Instagram handle';
  }

  if (contact_email && !EMAIL_RE.test(contact_email)) {
    errors.contact_email = 'Enter a valid email address';
  }

  if (phone) {
    const digits = phone.replace(/[^\d]/g, '');
    if (digits.length < 7 || digits.length > 15) {
      errors.phone = 'Enter a valid phone number';
    }
  }

  return errors;
}

function OrgBioEditModal({ isModalOpen, setIsModalOpen, orgId, org, onSaved }) {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { errors, setErrors, clearError } = useFieldErrors();
  const [bio, setBio] = useState(org?.bio || '');

  useEffect(() => {
    setBio(org?.bio || '');
  }, [org]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (isSaving) return;
    const formData = new FormData(e.target);
    const raw = Object.fromEntries(CONTACT_FIELDS.map(({ name }) => [name, formData.get(name)?.trim() || '']));

    const newErrors = validateFields(raw);
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const newInfo = {
      bio: formData.get('bio')?.trim() || null,
      website: raw.website ? normalizeWebsite(raw.website) : null,
      instagram: raw.instagram ? normalizeInstagram(raw.instagram) : null,
      contact_email: raw.contact_email || null,
      phone: raw.phone || null,
    };

    setIsSaving(true);
    try {
      await updateOrgInfo(orgId, newInfo);
      onSaved?.(newInfo);
      setIsModalOpen(false);
      showSuccess('Organization info saved.');
    } catch (error) {
      console.error('Error updating org info:', error);
      showError(`Failed to update organization info: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setIsModalOpen(false);
    setErrors({});
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      title="Edit Organization Info"
      description={
        <p className="text-xs text-slate-400 mt-1">
          A new, upgraded organization page featuring these details is coming soon for all users.
        </p>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <FormField label="Bio" htmlFor="bio">
            <ExpandableTextarea
              id="bio"
              name="bio"
              title="Bio"
              autoFocus
              value={bio}
              onChange={e => setBio(e.target.value)}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CONTACT_FIELDS.map(({ name, ...field }) => (
              <TextField
                key={name}
                name={name}
                {...field}
                defaultValue={org?.[name] || ''}
                error={errors[name]}
                onChange={() => clearError(name)}
              />
            ))}
          </div>
        </div>

        <ModalActions onCancel={handleClose} isLoading={isSaving} />
      </form>
    </Modal>
  );
}

export default OrgBioEditModal;
